<template>
  <div class="space-y-6">
    <!-- YouTube -->
    <div class="bg-white rounded-xl border border-gray-200/60 card-shadow overflow-hidden">
      <!-- Card header -->
      <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
            <svg class="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          </div>
          <div>
            <h3 class="text-[15px] font-semibold text-gray-900">YouTube</h3>
            <p class="text-sm text-gray-400 mt-0.5">Publish content directly to YouTube channels</p>
          </div>
        </div>
        <span v-if="ytChannels.length" class="badge-live">{{ ytChannels.length }} Connected</span>
        <span v-else-if="ytCredentials.has_credentials" class="badge-idle">No channels</span>
        <span v-else class="badge-error">Not configured</span>
      </div>

      <!-- Inner tabs -->
      <div class="px-6 border-b border-gray-100">
        <div class="flex gap-1">
          <button @click="ytTab = 'channels'" :class="['px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors !rounded-none', ytTab === 'channels' ? 'border-[var(--color-primary)] text-[var(--color-primary)]' : 'border-transparent text-gray-400 hover:text-gray-600']">Channels</button>
          <button @click="ytTab = 'credentials'" :class="['px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors !rounded-none', ytTab === 'credentials' ? 'border-[var(--color-primary)] text-[var(--color-primary)]' : 'border-transparent text-gray-400 hover:text-gray-600']">Credentials</button>
        </div>
      </div>

      <!-- Channels tab -->
      <div v-if="ytTab === 'channels'">
        <div v-if="ytChannels.length" class="divide-y divide-gray-100">
          <div v-for="channel in ytChannels" :key="channel.id" class="flex items-center justify-between px-6 py-4 hover:bg-gray-50/50 transition-colors">
            <div class="flex items-center gap-3 min-w-0">
              <img v-if="channel.thumbnail" :src="channel.thumbnail" class="w-10 h-10 rounded-full object-cover shrink-0" :alt="channel.name" />
              <div v-else class="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <span class="text-sm font-bold text-red-500">{{ channel.name?.charAt(0) }}</span>
              </div>
              <div class="min-w-0">
                <p class="text-sm font-medium text-gray-900">{{ channel.name }}</p>
                <p class="text-xs font-data text-gray-400">{{ channel.channel_id }}</p>
              </div>
            </div>
            <button @click="removeChannel(channel)" class="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Remove">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
            </button>
          </div>
        </div>
        <div v-else class="px-6 py-12 text-center">
          <p class="text-sm text-gray-500 mb-1">No channels connected yet</p>
          <p v-if="!ytCredentials.has_credentials" class="text-xs text-amber-500">
            <button @click="ytTab = 'credentials'" class="underline !rounded-none">Configure credentials</button> first
          </p>
        </div>
        <!-- Connect button -->
        <div class="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button @click="connectYouTube" :disabled="ytConnecting || !ytCredentials.has_credentials" class="btn-primary" :title="!ytCredentials.has_credentials ? 'Configure credentials first' : ''">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {{ ytConnecting ? 'Connecting...' : 'Connect Channel' }}
          </button>
        </div>
      </div>

      <!-- Credentials tab -->
      <div v-if="ytTab === 'credentials'" class="p-6 space-y-4">
        <!-- Info block -->
        <div class="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-100">
          <svg class="w-5 h-5 text-blue-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          <div class="text-sm text-blue-700">
            <p class="font-semibold mb-1">Google & YouTube</p>
            <p class="text-blue-600">In Google Developers Console create a new Web Application inside your project (<span class="font-medium">API & Auth > Credentials > New Client ID > Web Application</span>) using the following settings:</p>
            <ul class="mt-2 space-y-1 text-blue-600">
              <li><span class="font-medium">JavaScript Origins:</span> <code class="text-xs bg-blue-100 px-1.5 py-0.5 rounded">{{ currentHost }}</code></li>
              <li><span class="font-medium">Redirect URI:</span> <code class="text-xs bg-blue-100 px-1.5 py-0.5 rounded">{{ defaultRedirectUri }}</code></li>
              <li><span class="font-medium">Client ID:</span> Copy the OAuth 2.0 Client ID</li>
              <li><span class="font-medium">Client Secret:</span> Copy the OAuth 2.0 Client Secret</li>
              <li><span class="font-medium">Enable:</span> YouTube Data API v3 on application dashboard</li>
            </ul>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Client ID</label>
          <input v-model="ytCredentials.client_id" type="text" placeholder="e.g. 123456789.apps.googleusercontent.com" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Client Secret</label>
          <input v-model="ytCredentials.client_secret" type="text" placeholder="Your OAuth client secret" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Redirect URI</label>
          <input v-model="ytCredentials.redirect_uri" type="text" :placeholder="defaultRedirectUri" />
          <p class="text-xs text-gray-400 mt-1">Default: {{ defaultRedirectUri }}</p>
        </div>
        <div class="flex justify-end">
          <button @click="saveYtCredentials" :disabled="savingYtCredentials" class="btn-primary">
            <svg v-if="!savingYtCredentials" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            {{ savingYtCredentials ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Future platforms -->
    <div class="bg-white rounded-xl border border-gray-200/60 card-shadow overflow-hidden opacity-40 pointer-events-none">
      <div class="px-6 py-3 flex items-center gap-3">
        <div class="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
          <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor"/></svg>
        </div>
        <div>
          <h3 class="text-[15px] font-semibold text-gray-500">Instagram</h3>
          <p class="text-sm text-gray-400 mt-0.5">Coming soon</p>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-xl border border-gray-200/60 card-shadow overflow-hidden opacity-40 pointer-events-none">
      <div class="px-6 py-3 flex items-center gap-3">
        <div class="w-9 h-9 rounded-lg bg-black flex items-center justify-center shrink-0">
          <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.46a8.18 8.18 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.11z"/></svg>
        </div>
        <div>
          <h3 class="text-[15px] font-semibold text-gray-500">TikTok</h3>
          <p class="text-sm text-gray-400 mt-0.5">Coming soon</p>
        </div>
      </div>
    </div>

    <!-- Remove confirm -->
    <ConfirmModal
      :show="showRemoveModal"
      :loading="removing"
      title="Remove YouTube Channel"
      :message="`Are you sure you want to remove '${channelToRemove?.name}'?`"
      confirm-text="Remove"
      :danger="true"
      @confirm="doRemoveChannel"
      @cancel="showRemoveModal = false"
    />
  </div>
</template>

<script>
import ConfirmModal from '../../components/ConfirmModal.vue'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

export default {
  name: 'PublishingView',
  components: { ConfirmModal },

  data() {
    return {
      // YouTube
      ytTab: 'channels',
      ytCredentials: { client_id: '', client_secret: '', redirect_uri: '', has_credentials: false },
      savingYtCredentials: false,
      ytChannels: [],
      ytConnecting: false,
      // Remove
      showRemoveModal: false,
      removing: false,
      channelToRemove: null
    }
  },

  computed: {
    currentHost() {
      return window.location.origin
    },
    defaultRedirectUri() {
      return `${window.location.origin}/publishing`
    }
  },

  async mounted() {
    await Promise.all([this.loadYtSettings(), this.loadYtChannels()])
    this.handleOAuthCallback()
  },

  methods: {
    // YouTube credentials
    async loadYtSettings() {
      try {
        const res = await api.get('/integrations/publishing/youtube/settings', { silent: true })
        this.ytCredentials = res.data || this.ytCredentials
      } catch { /* silent */ }
    },

    async saveYtCredentials() {
      this.savingYtCredentials = true
      try {
        await api.put('/integrations/publishing/youtube/settings', {
          client_id: this.ytCredentials.client_id,
          client_secret: this.ytCredentials.client_secret,
          redirect_uri: this.ytCredentials.redirect_uri || this.defaultRedirectUri
        })
        notifySuccess('YouTube credentials saved')
        this.ytCredentials.has_credentials = !!(this.ytCredentials.client_id && this.ytCredentials.client_secret)
      } catch (err) {
        notifyError(err?.message || 'Failed to save credentials')
      } finally { this.savingYtCredentials = false }
    },

    // YouTube channels
    async loadYtChannels() {
      try {
        const res = await api.get('/integrations/publishing/youtube/channels', { silent: true })
        this.ytChannels = res.data?.channels || []
      } catch { /* silent */ }
    },

    async connectYouTube() {
      this.ytConnecting = true
      try {
        const res = await api.get('/integrations/publishing/youtube/auth')
        window.location.href = res.data?.authUrl
      } catch (err) {
        this.ytConnecting = false
        notifyError(err?.message || 'Failed to start YouTube authorization')
      }
    },

    async handleOAuthCallback() {
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')
      const state = urlParams.get('state')
      if (code && state === 'youtube_oauth') {
        window.history.replaceState({}, document.title, window.location.pathname)
        this.ytTab = 'channels'
        try {
          const res = await api.post('/integrations/publishing/youtube/callback', { code })
          const data = res.data || {}
          notifySuccess(data.isNew ? `Connected "${data.channelName}"` : `Reconnected "${data.channelName}"`)
          await this.loadYtChannels()
        } catch (err) {
          notifyError(err?.message || 'Failed to connect YouTube channel')
        }
      }
    },

    removeChannel(channel) {
      this.channelToRemove = channel
      this.showRemoveModal = true
    },

    async doRemoveChannel() {
      if (!this.channelToRemove) return
      this.removing = true
      try {
        await api.delete(`/integrations/publishing/youtube/${this.channelToRemove.id}`)
        notifySuccess('Channel removed')
        this.showRemoveModal = false
        this.channelToRemove = null
        await this.loadYtChannels()
      } catch (err) {
        notifyError(err?.message || 'Failed to remove channel')
      } finally { this.removing = false }
    }
  }
}
</script>
